<template>
    <Dialog :visible="visible" @update:visible="v => $emit('update:visible', v)" modal :header="title"
        :style="{ width: '60vw' }" :breakpoints="{ '1199px': '85vw', '575px': '95vw' }" :closable="!sending">
        <div v-if="loading" class="text-center py-5">
            <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem;"></i>
            <p class="text-muted mt-2 mb-0">جاري تحميل قائمة المستلمين...</p>
        </div>

        <template v-else>
            <div v-if="blockedMessage" class="alert alert-warning mb-0">{{ blockedMessage }}</div>

            <template v-else>
                <!-- ── Summary ── -->
                <div class="row g-2 mb-3 text-center">
                    <div class="col-6 col-md-2">
                        <div class="np-stat"><div class="np-stat__value">{{ summary.total_eligible ?? 0 }}</div><div class="np-stat__label">إجمالي المستهدفين</div></div>
                    </div>
                    <div class="col-6 col-md-2">
                        <div class="np-stat np-stat--success"><div class="np-stat__value">{{ summary.total_recipients ?? 0 }}</div><div class="np-stat__label">أولياء الأمور المستهدفون</div></div>
                    </div>
                    <div class="col-6 col-md-2">
                        <div class="np-stat np-stat--whatsapp"><div class="np-stat__value">{{ summary.whatsapp ?? 0 }}</div><div class="np-stat__label">WhatsApp</div></div>
                    </div>
                    <div class="col-6 col-md-2">
                        <div class="np-stat np-stat--sms"><div class="np-stat__value">{{ summary.sms ?? 0 }}</div><div class="np-stat__label">SMS</div></div>
                    </div>
                    <div class="col-6 col-md-2">
                        <div class="np-stat np-stat--warning"><div class="np-stat__value">{{ summary.no_phone ?? 0 }}</div><div class="np-stat__label">بدون رقم ولي أمر</div></div>
                    </div>
                    <div class="col-6 col-md-2">
                        <!-- Clickable — opens the full excluded-students breakdown (online +
                             no-phone) with each student's own reason, never just a bare count. -->
                        <div class="np-stat np-stat--muted np-stat--clickable" role="button" tabindex="0"
                            @click="excludedDialogVisible = true" @keyup.enter="excludedDialogVisible = true">
                            <div class="np-stat__value">{{ summary.excluded ?? 0 }}</div>
                            <div class="np-stat__label">مستبعدون <i class="pi pi-external-link np-stat__icon"></i></div>
                        </div>
                    </div>
                    <div class="col-6 col-md-2" v-if="selectable">
                        <div class="np-stat np-stat--primary">
                            <div class="np-stat__value">{{ selected.size }}</div>
                            <div class="np-stat__label">المحدد للإرسال</div>
                        </div>
                    </div>
                </div>

                <!-- ── Sending channel selector ── -->
                <div class="np-channel mb-3">
                    <label class="fw-semibold d-block mb-1">طريقة الإرسال</label>
                    <div class="btn-group" role="group">
                        <button type="button" class="btn" :class="channel === 'whatsapp' ? 'btn-success' : 'btn-outline-success'"
                            :disabled="sending" @click="$emit('update:channel', 'whatsapp')">
                            <i class="pi pi-whatsapp me-1"></i>WhatsApp
                        </button>
                        <button type="button" class="btn" :class="channel === 'sms' ? 'btn-primary' : 'btn-outline-primary'"
                            :disabled="sending" @click="$emit('update:channel', 'sms')">
                            <i class="pi pi-comment me-1"></i>SMS
                        </button>
                    </div>
                    <p v-if="selectable" class="text-muted small mt-1 mb-0">
                        عند تغيير طريقة الإرسال يتم إعادة حساب المستحقين، ويُحتفظ باختيارك الحالي طالما بقي الطالب مؤهلاً.
                    </p>
                </div>

                <div v-if="channelBlocked" class="alert alert-danger py-2 small">
                    <i class="pi pi-exclamation-triangle me-1"></i>
                    WhatsApp غير متصل — لن يتم إرسال أي رسائل عبر واتساب الآن. اختر SMS للإرسال الآن، أو اربط واتساب أولاً.
                </div>

                <!-- ── Recipients table ── -->
                <div class="np-table-wrap">
                    <table class="table table-sm table-striped mb-0">
                        <thead>
                            <tr>
                                <th v-if="selectable" class="np-check-col">
                                    <input type="checkbox" class="form-check-input" aria-label="تحديد الكل"
                                        :checked="allEligibleSelected" :disabled="!recipients.length"
                                        @change="toggleSelectAll($event.target.checked)" />
                                </th>
                                <th>الطالب</th>
                                <th>رقم الطالب</th>
                                <th>نظام الدراسة</th>
                                <th>رقم ولي الأمر</th>
                                <th>القناة</th>
                                <th>السبب</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="r in recipients" :key="r.student_id">
                                <td v-if="selectable" class="np-check-col">
                                    <input type="checkbox" class="form-check-input" :aria-label="'تحديد ' + r.student_name"
                                        :checked="selected.has(r.student_id)" @change="toggleOne(r.student_id)" />
                                </td>
                                <td>{{ r.student_name }}</td>
                                <td>{{ r.student_number }}</td>
                                <td>{{ studyModeLabel(r.study_mode) }}</td>
                                <td dir="ltr">{{ r.parent_phone }}</td>
                                <td>
                                    <span class="badge" :class="r.channel === 'whatsapp' ? 'bg-success' : 'bg-info text-dark'">
                                        {{ r.channel === 'whatsapp' ? 'WhatsApp' : 'SMS' }}
                                    </span>
                                </td>
                                <td>{{ r.reason }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p v-if="!recipients.length" class="text-muted text-center py-4 mb-0">
                        لا يوجد طلاب مستحقون للإشعار حالياً بحسب الفلاتر الحالية.
                    </p>
                </div>

                <p v-if="excluded.length" class="text-muted small mt-2 mb-0">
                    <i class="pi pi-info-circle me-1"></i>
                    يوجد {{ excluded.length }} طالب مستبعد من هذا الإشعار —
                    <a href="#" @click.prevent="excludedDialogVisible = true">عرض التفاصيل</a>.
                </p>

                <div v-if="selectable && recipients.length && !selected.size" class="alert alert-warning py-2 small mt-2 mb-0">
                    <i class="pi pi-exclamation-triangle me-1"></i>
                    يرجى اختيار طالب واحد على الأقل للإرسال.
                </div>
            </template>
        </template>

        <template #footer>
            <button type="button" class="btn btn-secondary" @click="$emit('cancel')" :disabled="sending">إلغاء</button>
            <button type="button" class="btn btn-warning" @click="onConfirmClick"
                :disabled="sending || loading || !!blockedMessage || channelBlocked || !recipients.length || (selectable && !selected.size)">
                <i class="pi pi-spin pi-spinner-dotted me-1" v-if="sending"></i>
                {{ sending ? 'جاري الإرسال...' : confirmLabel }}
            </button>
        </template>
    </Dialog>

    <ExcludedStudentsDialog v-model:visible="excludedDialogVisible" :excluded="excluded" />
</template>

<script>
import Dialog from 'primevue/dialog';
import ExcludedStudentsDialog from './ExcludedStudentsDialog.vue';

export default {
    name: 'NotificationPreviewDialog',
    components: { Dialog, ExcludedStudentsDialog },
    props: {
        visible: { type: Boolean, default: false },
        title: { type: String, default: 'معاينة الإشعار' },
        loading: { type: Boolean, default: false },
        sending: { type: Boolean, default: false },
        blockedMessage: { type: String, default: null },
        whatsappAvailable: { type: Boolean, default: true },
        // Admin-selected sending channel — controls both the preview's
        // "القناة" column and the actual send method. No auto-fallback: if
        // 'whatsapp' is picked while WhatsApp isn't connected, sending is
        // blocked (channelBlocked) rather than silently switching to SMS.
        channel: { type: String, default: 'whatsapp' },
        summary: { type: Object, default: () => ({}) },
        recipients: { type: Array, default: () => [] },
        // Unified excluded list from the backend resolver (online-excluded +
        // no-parent-phone students, each with its own 'reason') — the exact
        // same collection summary.excluded is counted from, shown in full
        // via the "مستبعدون" card's details dialog. Never a silent drop.
        excluded: { type: Array, default: () => [] },
        // Opt-in: adds a checkbox column, a "select all" header checkbox, and
        // a live "المحدد للإرسال" count. When false (default), behaves exactly
        // as before — 'confirm' fires with no payload and the caller sends to
        // every recipient. Only the attendance flow enables this for now.
        selectable: { type: Boolean, default: false },
    },
    emits: ['update:visible', 'update:channel', 'confirm', 'cancel'],
    data() {
        return {
            excludedDialogVisible: false,
            // Reactive Set of selected student_ids — Vue 3 tracks Set
            // mutations (add/delete/clear) natively, no extra wrapping needed.
            selected: new Set(),
            // True right after the dialog is (re)opened, until the next
            // 'recipients' update — that update is the FRESH preview load for
            // this open, so it defaults to "everyone selected" (matches the
            // previous send-to-everyone behavior). Any 'recipients' update
            // AFTER that (e.g. a channel switch while the dialog stays open)
            // only reconciles the existing selection instead.
            awaitingFreshSelection: false,
        };
    },
    computed: {
        channelBlocked() {
            return this.channel === 'whatsapp' && !this.whatsappAvailable;
        },
        allEligibleSelected() {
            return this.recipients.length > 0 && this.recipients.every(r => this.selected.has(r.student_id));
        },
        confirmLabel() {
            const channelName = this.channel === 'whatsapp' ? 'WhatsApp' : 'SMS';
            return this.selectable
                ? `تأكيد وإرسال ${channelName} (${this.selected.size})`
                : `تأكيد وإرسال عبر ${channelName}`;
        },
    },
    watch: {
        visible(v) {
            if (v && this.selectable) this.awaitingFreshSelection = true;
        },
        recipients(newRecipients) {
            if (!this.selectable) return;
            if (this.awaitingFreshSelection) {
                this.selected = new Set(newRecipients.map(r => r.student_id));
                this.awaitingFreshSelection = false;
                return;
            }
            // Reconcile only — e.g. after a channel switch — never re-select
            // everyone, so a deliberate deselection survives the recompute.
            const validIds = new Set(newRecipients.map(r => r.student_id));
            Array.from(this.selected).forEach(id => {
                if (!validIds.has(id)) this.selected.delete(id);
            });
        },
    },
    methods: {
        studyModeLabel(mode) {
            return { offline: 'حضوري', hybrid: 'هجين', online: 'أونلاين' }[mode] ?? '—';
        },
        toggleOne(id) {
            if (this.selected.has(id)) this.selected.delete(id);
            else this.selected.add(id);
        },
        toggleSelectAll(checked) {
            if (checked) this.recipients.forEach(r => this.selected.add(r.student_id));
            else this.selected.clear();
        },
        onConfirmClick() {
            this.$emit('confirm', this.selectable ? Array.from(this.selected) : null);
        },
    },
};
</script>

<style scoped>
.np-stat {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 0.5rem 0.25rem;
}
.np-stat__value { font-size: 1.25rem; font-weight: 700; }
.np-stat__label { font-size: 0.72rem; color: #6c757d; }
.np-stat--success { background: #eafaf0; }
.np-stat--success .np-stat__value { color: #198754; }
.np-stat--whatsapp { background: #eafaf0; }
.np-stat--whatsapp .np-stat__value { color: #25d366; }
.np-stat--sms { background: #e7f5ff; }
.np-stat--sms .np-stat__value { color: #0d6efd; }
.np-stat--warning { background: #fff8e6; }
.np-stat--warning .np-stat__value { color: #b45309; }
.np-stat--muted { background: #f1f1f1; }
.np-stat--muted .np-stat__value { color: #6c757d; }
.np-stat--primary { background: #eef2ff; }
.np-stat--primary .np-stat__value { color: #4338ca; }
.np-stat--clickable {
    cursor: pointer;
    transition: background-color .15s ease, box-shadow .15s ease;
}
.np-stat--clickable:hover,
.np-stat--clickable:focus-visible {
    background: #e2e3e5;
    box-shadow: inset 0 0 0 1px #adb5bd;
    outline: none;
}
.np-stat__icon { font-size: 0.65rem; vertical-align: middle; }

.np-table-wrap {
    max-height: 42vh;
    overflow-y: auto;
    overflow-x: auto;
    border: 1px solid #e9ecef;
    border-radius: 6px;
}
.np-table-wrap table thead th {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 1;
}
.np-check-col {
    width: 2.5rem;
    text-align: center;
}
</style>
